import type { IHttpClient } from '@wix/yoshi-flow-editor';
import { PopulatedItemClientFactory } from '@wix/restaurants-populated-item-client/web';
import type { CursorQueryFilter, Item } from 'root/types/item';
import type { ErrorHandlerPublicAPI } from '@wix/error-handler';
import type { PopulatedItemClient } from '@wix/restaurants-populated-item-client/dist/types/types';

type FileInfoType = Partial<{
  width: number;
  height: number;
  file_name: string;
}>;

type ItemQueryResponse = {
  items: Required<Item[]>;
  next: (() => Promise<ItemQueryResponse>) | undefined;
};

const BATCH_SIZE = 500;

export class ItemsClient {
  itemsClient: PopulatedItemClient<{
    labels: true;
    modifierGroups: true;
    modifiers: true;
    priceVariants: true;
  }>;

  constructor(private httpClient: IHttpClient, private errorHandler: ErrorHandlerPublicAPI) {
    this.itemsClient = PopulatedItemClientFactory({
      httpClient,
      populationLevel: {
        labels: true,
        modifierGroups: true,
        modifiers: true,
        priceVariants: true,
      },
    });
  }

  private async query(
    filter: CursorQueryFilter | undefined = undefined,
    cursor: string | null = null
  ): Promise<ItemQueryResponse> {
    const { withErrorHandler } = this.errorHandler;

    const response = await withErrorHandler(
      () =>
        this.itemsClient.query({
          filter,
          cursorPaging: {
            limit: 500,
            cursor,
          },
        }),
      { errorCodesMap: {} }
    );
    if (response.hasError) {
      throw response.error;
    }
    const { pagingMetadata, data } = response.data;
    return {
      items: (data || []) as Item[],
      next: pagingMetadata?.hasNext
        ? this.query.bind(this, undefined, pagingMetadata.cursors?.next)
        : undefined,
    };
  }

  private async fetchBatch(itemIds?: string[]) {
    const { getResolvedError } = this.errorHandler;
    try {
      let response = await this.query({
        visible: { $ne: false },
        ...(itemIds ? { id: { $in: itemIds } } : {}),
      });
      const items = [...response.items!];
      while (response.next) {
        response = await response.next();
        items.push(...response.items!);
      }
      return items;
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      throw e;
    }
  }

  fetchAllItems = async (itemIds?: string[]) => {
    const items = [];
    if (itemIds) {
      let itemsLeft = itemIds.length;
      const batchPromises = [];
      while (itemsLeft > 0) {
        const batch = itemIds.splice(0, BATCH_SIZE);
        batchPromises.push(this.fetchBatch(batch));
        itemsLeft -= BATCH_SIZE;
      }
      const batches = await Promise.all(batchPromises);
      items.push(...batches.flat());
    } else {
      items.push(...(await this.fetchBatch()));
    }
    return { items };
  };

  getItemById = async (itemId: string) => {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const response = await withErrorHandler(() => this.itemsClient.getById(itemId), {
        errorCodesMap: {},
      });
      if (response.hasError) {
        const error = getResolvedError(response.error);
        console.error(error);
        return {};
      }
      return response.data;
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return {};
    }
  };

  getImgsData = async (
    fileIds: string[],
    metaSiteId: string
  ): Promise<{ id: string; width: number; height: number }[]> => {
    const { withErrorHandler, getResolvedError } = this.errorHandler;
    try {
      const url = `https://files.wix.com/site/media/files/multi/info`;
      const { data } = await withErrorHandler(
        () =>
          this.httpClient.post(url, {
            files: fileIds,
            site_id: metaSiteId,
          }),
        { errorCodesMap: {} }
      );
      const { files = [] } = data as Partial<{
        files: FileInfoType[];
      }>;
      return files.map((file: FileInfoType) => {
        const { file_name = '', width = 0, height = 0 } = file;
        return { id: file_name, width, height };
      });
    } catch (e: unknown) {
      const error = getResolvedError(e);
      console.error(error);
      return [];
    }
  };
}
